import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Avatar,
  Stack,
  Skeleton,
  Tooltip,
  IconButton,
  InputBase,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
} from "@mui/material";
import { BsWhatsapp } from "react-icons/bs";
import {
  FiUsers,
  FiX,
  FiPhone,
  FiPause,
  FiPlay,
  FiRefreshCw,
  FiAlertCircle,
  FiSend,
  FiInbox,
  FiShield,
  FiMessageCircle,
  FiChevronRight,
} from "react-icons/fi";
import { BASE_URL } from "../../constants/url";

// ---- Design tokens (kept local so the component stays self-contained) ----
// Brand colors stay identical in both modes; only the surfaces/ink flip so the
// page follows dark mode instead of staying white.
const brandTokens = {
  fb: "#1877F2",
  fbDark: "#0F5FCC",
  wa: "#25D366",
  waDark: "#1EBE57",
  waGlow: "rgba(37,211,102,0.18)",
  success: "#16A34A",
  warning: "#F59E0B",
  danger: "#D32F2F",
};

const lightTokens = {
  ...brandTokens,
  ink: "#14171F",
  inkMuted: "#5B6472",
  inkFaint: "#8A93A3",
  border: "#E4E7EC",
  surface: "#FFFFFF",
  page: "#F8F9FB",
  waLight: "#E8FDF0",
  chatBg: "#F4F5F7",
  successBg: "#EAF8EF",
  warningBg: "#FEF9E7",
  dangerBg: "#FDECEA",
  soft: "rgba(20,23,31,0.06)",
  softFaint: "rgba(20,23,31,0.04)",
  hoverTint: "rgba(20,23,31,0.03)",
};

const darkTokens = {
  ...brandTokens,
  ink: "#E7EAF0",
  inkMuted: "#A4ACBB",
  inkFaint: "#7F8897",
  border: "#2B313C",
  surface: "#1C212B",
  page: "#12161F",
  waLight: "#152E22",
  chatBg: "#171C25",
  successBg: "rgba(22,163,74,0.16)",
  warningBg: "rgba(245,158,11,0.14)",
  dangerBg: "rgba(211,47,47,0.18)",
  soft: "rgba(255,255,255,0.07)",
  softFaint: "rgba(255,255,255,0.045)",
  hoverTint: "rgba(255,255,255,0.04)",
};

const getTokens = (isDark) => (isDark ? darkTokens : lightTokens);

// Components read the palette through this hook so they re-render on mode change.
export const useTokens = () => {
  const theme = useTheme();
  return getTokens(theme.palette.mode === "dark");
};

// ---- WhatsApp Embedded Signup (Meta Embedded Signup via FB.login) ----
export const FB_APP_ID = "955470040527558";
export const FB_CONFIG_ID = "1469185311931451";
export const FB_SDK_VERSION = "v23.0";

let fbSdkPromise = null;
export function loadFbSdk() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.FB) return Promise.resolve();
  if (fbSdkPromise) return fbSdkPromise;
  fbSdkPromise = new Promise((resolve, reject) => {
    window.fbAsyncInit = () => {
      window.FB.init({
        appId: FB_APP_ID,
        cookie: true,
        xfbml: false,
        version: FB_SDK_VERSION,
      });
      resolve();
    };
    const script = document.createElement("script");
    script.id = "facebook-jssdk";
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.onerror = () => {
      fbSdkPromise = null;
      reject(new Error("Failed to load Facebook SDK"));
    };
    document.body.appendChild(script);
  });
  return fbSdkPromise;
}

export function LiveDot() {
  const tokens = useTokens();
  return (
    <Box
      sx={{
        position: "relative",
        width: 8,
        height: 8,
        borderRadius: "50%",
        bgcolor: tokens.success,
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          bgcolor: tokens.success,
          animation: "pulseDot 1.8s ease-out infinite",
        },
        "@keyframes pulseDot": {
          "0%": { transform: "scale(1)", opacity: 0.6 },
          "100%": { transform: "scale(2.6)", opacity: 0 },
        },
      }}
    />
  );
}

// ---- Status chip used in the connected header area ----
export function StatusChip({ isConnected = true }) {
  const tokens = useTokens();
  if (!isConnected) return null;
  return (
    <Chip
      icon={<LiveDot />}
      label={isConnected ? "Connected" : "Connected"}
      size="small"
      sx={{
        bgcolor: tokens.successBg,
        color: tokens.success,
        fontWeight: 700,
        pl: 0.5,
        "& .MuiChip-icon": { ml: 1, mr: -0.5 },
      }}
    />
  );
}

// ---- Section header row used in both cards ----
export function CardHeaderRow({ icon, iconBg, iconShadow, title, subtitle, children }) {
  const tokens = useTokens();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 2.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: iconBg,
            boxShadow: iconShadow,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: tokens.ink }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
            {subtitle}
          </Typography>
        </Box>
      </Box>
      {children}
    </Box>
  );
}

// ---- Empty state shown when no pages / not connected ----
export function EmptyState({ icon, title, caption }) {
  const tokens = useTokens();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        py: 4,
        px: 2,
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: tokens.softFaint,
          mb: 1.5,
        }}
      >
        {icon}
      </Box>
      <Typography variant="body2" fontWeight={700} sx={{ color: tokens.ink, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="caption" sx={{ color: tokens.inkFaint, maxWidth: 320 }}>
        {caption}
      </Typography>
    </Box>
  );
}

// ================================================================
// TEAM INBOX HELPERS
// ================================================================
function initialsOf(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  return parts
    .slice(0, 2)
    .map((w) => (w[0] || "").toUpperCase())
    .join("");
}

const AVATAR_COLORS = ["#1877F2", "#7C3AED", "#0EA5E9", "#059669", "#D97706", "#DB2777", "#475569"];
function colorFor(id) {
  const s = String(id ?? "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function prettyPhone(phone) {
  if (!phone) return "—";
  const p = String(phone);
  return p.startsWith("+") ? p : `+${p}`;
}

function fmtTime(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

function fmtFullTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString([], {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function statusColor(status, tokens) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return tokens.success;
  if (s === "paused" || s === "inactive") return tokens.warning;
  return tokens.inkFaint;
}

// The team conversation endpoint reuses the agent conversation shape —
// normalize across the common variants so the UI stays resilient.
function normalizeMessages(data) {
  const raw = Array.isArray(data)
    ? data
    : data?.messages || data?.data || data?.conversation?.messages || [];
  return raw
    .map((m, i) => ({
      id: m.id ?? m.messageId ?? m.wamid ?? `msg-${i}`,
      text: m.message ?? m.text ?? m.body ?? "",
      role: m.sentByRole ?? m.role ?? (m.fromMe || m.outgoing ? "Agent" : "Customer"),
      ts: m.sentAt ?? m.timestamp ?? m.createdAt ?? m.lastMessageAt ?? null,
    }))
    .filter((m) => m.text);
}

function AgentAvatar({ name, id, size = 32 }) {
  return (
    <Avatar
      sx={{
        width: size,
        height: size,
        fontSize: size * 0.38,
        fontWeight: 700,
        color: "#fff",
        bgcolor: colorFor(id),
      }}
    >
      {initialsOf(name)}
    </Avatar>
  );
}

function PhoneAvatar({ size = 32 }) {
  const tokens = useTokens();
  return (
    <Avatar
      sx={{
        width: size,
        height: size,
        bgcolor: tokens.wa,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <FiPhone size={size * 0.45} />
    </Avatar>
  );
}

function StatusDot({ status }) {
  const tokens = useTokens();
  return (
    <Box
      sx={{
        width: 7,
        height: 7,
        borderRadius: "50%",
        bgcolor: statusColor(status, tokens),
        flexShrink: 0,
      }}
    />
  );
}

function StatusPill({ status }) {
  const tokens = useTokens();
  return (
    <Chip
      label={status || "—"}
      size="small"
      icon={<StatusDot status={status} />}
      sx={{
        height: 20,
        fontSize: 11,
        fontWeight: 700,
        bgcolor: tokens.soft,
        color: tokens.inkMuted,
        "& .MuiChip-icon": { ml: 0.75, mr: -0.5 },
      }}
    />
  );
}

// ================================================================
// TEAM INBOX — agency owner sees & joins every agent conversation
// ================================================================
function TeamInbox({ agencyId, token, onError }) {
  const tokens = useTokens();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeConv, setActiveConv] = useState(null); // { id, phoneNumber, status, agentName }
  const [messages, setMessages] = useState([]);
  const [convLoading, setConvLoading] = useState(false);
  const [convError, setConvError] = useState(null);
  const [refreshingConv, setRefreshingConv] = useState(false);
  const [refreshingList, setRefreshingList] = useState(false);

  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const headers = useCallback(
    () => ({ Authorization: `Bearer ${token}`, userid: agencyId }),
    [token, agencyId]
  );

  // ── Load the agents + their conversations ────────────────────
  const fetchTeam = useCallback(
    async (silent = false) => {
      if (!agencyId) return;
      if (!silent) {
        setLoading(true);
      } else {
        setRefreshingList(true);
      }
      setError(null);
      try {
        const res = await fetch(`${BASE_URL}api/agentwhatsapp/team`, { headers: headers() });
        if (!res.ok) throw new Error("request failed");
        const data = await res.json();
        setAgents(Array.isArray(data) ? data : []);
      } catch {
        setAgents([]);
        setError("Could not load team conversations. Please try again.");
      } finally {
        setLoading(false);
        setRefreshingList(false);
      }
    },
    [agencyId, headers]
  );

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  // ── Load a single conversation ───────────────────────────────
  const loadConversation = useCallback(
    async (convId, { silent } = {}) => {
      if (!convId) return;
      if (!silent) {
        setConvLoading(true);
        setMessages([]);
      } else {
        setRefreshingConv(true);
      }
      try {
        const res = await fetch(`${BASE_URL}api/agentwhatsapp/team/conversation/${convId}`, {
          headers: headers(),
        });
        if (!res.ok) throw new Error("request failed");
        const data = await res.json();
        setMessages(normalizeMessages(data));
        setConvError(null);
      } catch {
        if (!silent) {
          setMessages([]);
          setConvError("Could not load this conversation. Please try again.");
        }
      } finally {
        setConvLoading(false);
        setRefreshingConv(false);
      }
    },
    [headers]
  );

  const openConversation = (agent, conv) => {
    setActiveConv({
      id: conv.id,
      phoneNumber: conv.phoneNumber,
      status: conv.status,
      agentName: agent.agentName || `Agent #${agent.agentId}`,
    });
    loadConversation(conv.id);
  };

  // ── Gentle polling so the owner sees new messages live ───────
  useEffect(() => {
    if (!activeConv?.id) return undefined;
    const interval = setInterval(() => {
      loadConversation(activeConv.id, { silent: true });
    }, 20000);
    return () => clearInterval(interval);
  }, [activeConv?.id, loadConversation]);

  // ── Owner sends a message through the AGENT's WhatsApp ───────
  const sendMessage = async () => {
    const message = draft.trim();
    if (!message || !activeConv?.id || sending) return;
    setSending(true);
    try {
      const res = await fetch(`${BASE_URL}api/agentwhatsapp/team/send/${activeConv.id}`, {
        method: "POST",
        headers: { ...headers(), "Content-Type": "application/json" },
        body: JSON.stringify({ message }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && (data.success || data.messageId)) {
        setDraft("");
        setMessages((prev) => [
          ...prev,
          {
            id: `local-${Date.now()}`,
            text: message,
            role: "Agency",
            ts: new Date().toISOString(),
          },
        ]);
        loadConversation(activeConv.id, { silent: true });
      } else {
        onError(data.error || "Message failed to send. Please try again.");
      }
    } catch {
      onError("Message failed to send. Please check your connection and try again.");
    } finally {
      setSending(false);
    }
  };

  // ── Keep the chat scrolled to the newest message ─────────────
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, convLoading, activeConv?.id]);

  const totalChats = agents.reduce(
    (sum, a) => sum + (Array.isArray(a.conversations) ? a.conversations.length : 0),
    0
  );

  return (
    <>
      <Divider sx={{ my: 3, borderColor: tokens.border }} />

      {/* ── Toolbar ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <FiInbox size={17} color={tokens.waDark} />
          <Typography variant="subtitle2" fontWeight={800} sx={{ color: tokens.ink }}>
            Team Inbox
          </Typography>
          <Chip
            label={`${totalChats} chat${totalChats === 1 ? "" : "s"} · ${agents.length} agent${
              agents.length === 1 ? "" : "s"
            }`}
            size="small"
            sx={{
              height: 20,
              fontSize: 11,
              fontWeight: 700,
              bgcolor: tokens.waLight,
              color: tokens.waDark,
            }}
          />
        </Stack>

        <Tooltip title="Refresh conversations">
          <span>
            <IconButton
              size="small"
              onClick={() => {
                fetchTeam(true);
                if (activeConv?.id) loadConversation(activeConv.id, { silent: true });
              }}
              disabled={refreshingList}
              sx={{
                color: tokens.inkMuted,
                border: `1px solid ${tokens.border}`,
                borderRadius: 1.5,
                ...(refreshingList
                  ? {
                      "& svg": { animation: "waSpin 0.9s linear infinite" },
                      "@keyframes waSpin": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" },
                      },
                    }
                  : {}),
              }}
            >
              <FiRefreshCw size={14} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* ── Two-pane inbox ── */}
      <Box
        sx={{
          border: `1px solid ${tokens.border}`,
          borderRadius: 3,
          overflow: "hidden",
          bgcolor: tokens.surface,
        }}
      >
        {loading ? (
          <Box sx={{ p: 1.5 }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={52}
                sx={{ mx: 0.5, my: 0.75, borderRadius: 2 }}
              />
            ))}
          </Box>
        ) : error ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
              py: 4,
            }}
          >
            <FiAlertCircle size={22} color={tokens.danger} />
            <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
              {error}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => fetchTeam()}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}
            >
              Retry
            </Button>
          </Box>
        ) : agents.length === 0 ? (
          <EmptyState
            icon={<FiUsers size={20} color={tokens.inkFaint} />}
            title="No agent conversations yet"
            caption="Chats from your agents' WhatsApp accounts will appear here as they come in."
          />
        ) : (
          <Grid container>
            {/* ── Left pane: agents & their conversations ── */}
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                borderRight: { md: `1px solid ${tokens.border}` },
                borderBottom: { xs: `1px solid ${tokens.border}`, md: "none" },
                maxHeight: { xs: 300, md: 480 },
                overflowY: "auto",
                py: 1,
              }}
            >
              {agents.map((agent) => {
                const convs = Array.isArray(agent.conversations) ? agent.conversations : [];
                return (
                  <Box key={agent.agentId} sx={{ mb: 0.5 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1.25}
                      sx={{ px: 1.75, pt: 1.25, pb: 0.75 }}
                    >
                      <AgentAvatar name={agent.agentName} id={agent.agentId} size={28} />
                      <Typography
                        variant="body2"
                        fontWeight={800}
                        noWrap
                        sx={{ color: tokens.ink, flex: 1 }}
                      >
                        {agent.agentName || `Agent #${agent.agentId}`}
                      </Typography>
                      <Chip
                        label={convs.length}
                        size="small"
                        sx={{
                          height: 18,
                          minWidth: 18,
                          fontSize: 11,
                          fontWeight: 700,
                          bgcolor: tokens.soft,
                          color: tokens.inkMuted,
                        }}
                      />
                    </Stack>

                    {convs.length === 0 ? (
                      <Typography
                        variant="caption"
                        sx={{ display: "block", color: tokens.inkFaint, px: 4.5, pb: 1 }}
                      >
                        No conversations yet
                      </Typography>
                    ) : (
                      convs.map((conv) => {
                        const isActive = activeConv?.id === conv.id;
                        return (
                          <Box
                            key={conv.id}
                            onClick={() => openConversation(agent, conv)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.25,
                              px: 1.5,
                              py: 1,
                              mx: 1,
                              mb: 0.5,
                              borderRadius: 2,
                              cursor: "pointer",
                              border: "1px solid",
                              borderColor: isActive ? "rgba(37,211,102,0.4)" : "transparent",
                              bgcolor: isActive ? tokens.waLight : "transparent",
                              transition: "all 0.15s ease",
                              "&:hover": {
                                bgcolor: isActive ? tokens.waLight : tokens.hoverTint,
                              },
                            }}
                          >
                            <PhoneAvatar size={32} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                noWrap
                                sx={{ color: tokens.ink }}
                              >
                                {prettyPhone(conv.phoneNumber)}
                              </Typography>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.5}
                                sx={{ mt: 0.25 }}
                              >
                                <StatusDot status={conv.status} />
                                <Typography variant="caption" sx={{ color: tokens.inkFaint }}>
                                  {conv.status || "—"}
                                </Typography>
                              </Stack>
                            </Box>
                            <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                              <Tooltip title={fmtFullTime(conv.lastMessageAt)}>
                                <Typography
                                  variant="caption"
                                  sx={{ color: tokens.inkFaint, whiteSpace: "nowrap" }}
                                >
                                  {fmtTime(conv.lastMessageAt)}
                                </Typography>
                              </Tooltip>
                              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.25 }}>
                                <FiChevronRight size={14} color={tokens.inkFaint} />
                              </Box>
                            </Box>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                );
              })}
            </Grid>

            {/* ── Right pane: chat ── */}
            <Grid item xs={12} md={8} sx={{ display: "flex", flexDirection: "column" }}>
              {!activeConv ? (
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <EmptyState
                    icon={<FiMessageCircle size={20} color={tokens.inkFaint} />}
                    title="Select a conversation"
                    caption="Pick an agent's chat on the left to read the conversation and reply as the agency."
                  />
                </Box>
              ) : (
                <>
                  {/* Chat header */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      px: 2,
                      py: 1.5,
                      borderBottom: `1px solid ${tokens.border}`,
                      bgcolor: tokens.surface,
                    }}
                  >
                    <PhoneAvatar size={36} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={800} noWrap sx={{ color: tokens.ink }}>
                        {prettyPhone(activeConv.phoneNumber)}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.25 }}>
                        <Typography variant="caption" sx={{ color: tokens.inkFaint }} noWrap>
                          via {activeConv.agentName}
                        </Typography>
                        <StatusPill status={activeConv.status} />
                      </Stack>
                    </Box>
                    <Tooltip title="Refresh messages">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => loadConversation(activeConv.id, { silent: true })}
                          disabled={refreshingConv}
                          sx={{
                            color: tokens.inkMuted,
                            ...(refreshingConv
                              ? {
                                  "& svg": { animation: "waSpin2 0.9s linear infinite" },
                                  "@keyframes waSpin2": {
                                    "0%": { transform: "rotate(0deg)" },
                                    "100%": { transform: "rotate(360deg)" },
                                  },
                                }
                              : {}),
                          }}
                        >
                          <FiRefreshCw size={15} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>

                  {/* Messages */}
                  <Box
                    sx={{
                      height: { xs: 300, md: 360 },
                      overflowY: "auto",
                      bgcolor: tokens.chatBg,
                      px: 2,
                      py: 2,
                    }}
                  >
                    {convLoading ? (
                      <Box
                        sx={{
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CircularProgress size={24} thickness={4} sx={{ color: tokens.wa }} />
                      </Box>
                    ) : convError ? (
                      <Box
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1.5,
                        }}
                      >
                        <FiAlertCircle size={22} color={tokens.danger} />
                        <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
                          {convError}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => loadConversation(activeConv.id)}
                          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}
                        >
                          Retry
                        </Button>
                      </Box>
                    ) : messages.length === 0 ? (
                      <Box
                        sx={{
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <EmptyState
                          icon={<FiMessageCircle size={20} color={tokens.inkFaint} />}
                          title="No messages yet"
                          caption="This conversation has no messages. Say hello as the agency below."
                        />
                      </Box>
                    ) : (
                      <Stack spacing={1.25}>
                        {messages.map((msg) => (
                          <MessageBubble key={msg.id} msg={msg} />
                        ))}
                      </Stack>
                    )}
                  </Box>

                  {/* Composer */}
                  <Box
                    sx={{
                      borderTop: `1px solid ${tokens.border}`,
                      bgcolor: tokens.surface,
                      p: 1.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
                      <InputBase
                        fullWidth
                        multiline
                        maxRows={4}
                        placeholder="Write a message — it will be sent from the agent's WhatsApp…"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            sendMessage();
                          }
                        }}
                        sx={{
                          bgcolor: tokens.chatBg,
                          borderRadius: 3,
                          px: 1.75,
                          py: 1,
                          fontSize: 14,
                          color: tokens.ink,
                        }}
                      />
                      <Tooltip title="Send via agent's WhatsApp">
                        <span>
                          <IconButton
                            onClick={sendMessage}
                            disabled={sending || !draft.trim()}
                            sx={{
                              bgcolor: tokens.wa,
                              color: "#fff",
                              "&:hover": { bgcolor: tokens.waDark },
                              "&.Mui-disabled": { bgcolor: "rgba(37,211,102,0.4)", color: "#fff" },
                              width: 40,
                              height: 40,
                            }}
                          >
                            {sending ? (
                              <CircularProgress size={17} thickness={5} sx={{ color: "#fff" }} />
                            ) : (
                              <FiSend size={16} />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.75}
                      sx={{ mt: 0.75, px: 0.5 }}
                    >
                      <FiShield size={11} color={tokens.inkFaint} />
                      <Typography variant="caption" sx={{ color: tokens.inkFaint }}>
                        Sent through the agent&apos;s WhatsApp account — logged as{" "}
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          &quot;Sent by Agency&quot;
                        </Box>{" "}
                        for audit. The agent can keep replying normally.
                      </Typography>
                    </Stack>
                  </Box>
                </>
              )}
            </Grid>
          </Grid>
        )}
      </Box>
    </>
  );
}

// ---- Chat bubble ----
function MessageBubble({ msg }) {
  const tokens = useTokens();
  const role = String(msg.role || "").toLowerCase();
  const outgoing =
    role === "agency" || role === "agent" || msg.fromMe === true || String(msg.direction || "").toLowerCase() === "out";
  const isAgency = role === "agency";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: outgoing ? "flex-end" : "flex-start",
        maxWidth: "78%",
        alignSelf: outgoing ? "flex-end" : "flex-start",
      }}
    >
      {isAgency && (
        <Chip
          icon={<FiShield size={10} />}
          label="Sent by Agency"
          size="small"
          sx={{
            height: 18,
            fontSize: 10,
            fontWeight: 700,
            mb: 0.5,
            bgcolor: "rgba(24,119,242,0.09)",
            color: tokens.fb,
            "& .MuiChip-icon": { ml: 0.75, mr: -0.5 },
          }}
        />
      )}
      <Box
        sx={{
          px: 1.75,
          py: 1.1,
          borderRadius: outgoing ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
          bgcolor: outgoing ? tokens.wa : tokens.surface,
          border: outgoing ? "none" : `1px solid ${tokens.border}`,
          boxShadow: "0 1px 1.5px rgba(20,23,31,0.06)",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: outgoing ? "#FFFFFF" : tokens.ink,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            lineHeight: 1.45,
          }}
        >
          {msg.text}
        </Typography>
      </Box>
      <Tooltip title={fmtFullTime(msg.ts)}>
        <Typography variant="caption" sx={{ color: tokens.inkFaint, mt: 0.35, px: 0.5 }}>
          {fmtTime(msg.ts)}
        </Typography>
      </Tooltip>
    </Box>
  );
}

// ================================================================
// AGENCY WHATSAPP HELPERS — shared headers + parsing
// ================================================================
// Every agency endpoint below sends:
//   Authorization: Bearer <agency token>
//   userid: <agencyId> (required on every endpoint)
//   Content-Type: application/json (on POST/PUT with body)
// ================================================================
async function agencyApi({ token, agencyId, path, method = "GET", body }) {
  const headers = {
    Authorization: `Bearer ${token}`,
    userid: agencyId,
    Accept: "application/json",
  };
  if (body || method === "POST" || method === "PUT") {
    headers["Content-Type"] = "application/json";
  }
  const res = await fetch(`${BASE_URL}${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
  let data = null;
  try { data = await res.json(); } catch (_) {}

  if (!res.ok) {
    const msg =
      typeof data === "string"
        ? data
        : data?.error ?? data?.reason ?? data?.Message ?? `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data;
}

// Normalize the two conversation shapes the agency inbox can return:
//   GET /agencywhatsapp/conversations (list) and
//   GET /agencywhatsapp/conversation/{id} (conversation + messages).
function normalizeAgencyMessages(data) {
  const raw = Array.isArray(data)
    ? data
    : data?.messages || data?.conversation?.messages || [];
  return raw
    .map((m, i) => ({
      id: m.Id ?? m.id ?? m.messageId ?? `msg-${i}`,
      text: m.Message ?? m.message ?? m.text ?? m.body ?? "",
      role:
        m.SentByRole ?? m.sentByRole ?? m.role ?? (m.SentByUserId || m.sentByUserId ? "Agency" : "Customer"),
      ts: m.CreatedAt ?? m.createdAt ?? m.timestamp ?? m.sentAt ?? null,
      direction: m.Direction ?? m.direction ?? null,
    }))
    .filter((m) => m.text);
}

// ---- Agency conversation list row ----
function AgencyConvRow({ conv, onClick }) {
  const tokens = useTokens();
  return (
    <Box
      onClick={() => onClick(conv)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 1.75,
        py: 1.1,
        mx: 1,
        mb: 0.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: tokens.border,
        cursor: "pointer",
        bgcolor: tokens.surface,
        transition: "all 0.15s ease",
        "&:hover": { bgcolor: tokens.hoverTint, borderColor: tokens.border },
      }}
    >
      <Avatar sx={{ bgcolor: tokens.wa, width: 40, height: 40, color: "#fff" }}>
        <FiPhone size={18} />
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} noWrap sx={{ color: tokens.ink }}>
          {prettyPhone(conv.PhoneNumber)}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.25 }}>
          <StatusDot status={conv.Status} />
          <Typography variant="caption" sx={{ color: tokens.inkFaint }}>
            {conv.Status || "—"}
          </Typography>
          {conv.LeadId != null && (
            <>
              <Typography variant="caption" sx={{ color: tokens.inkFaint }}>·</Typography>
              <Typography variant="caption" sx={{ color: tokens.inkFaint }}>
                Lead #{conv.LeadId}
              </Typography>
            </>
          )}
        </Stack>
      </Box>
      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
        <Tooltip title={fmtFullTime(conv.LastMessageAt)}>
          <Typography variant="caption" sx={{ color: tokens.inkFaint, whiteSpace: "nowrap" }}>
            {fmtTime(conv.LastMessageAt)}
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  );
}

// ================================================================
// AGENCY WHATSAPP SETTINGS CARD — connection status / toggle / template
// ================================================================
export function AgencyWhatsAppSettingsCard({ agencyId, token, onError, onDisconnected }) {
  const tokens = useTokens();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [status, setStatus] = useState(null); // null=loading, false=not connected, object=connected
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!token || !agencyId) return;
    try {
      const data = await agencyApi({ token, agencyId, path: "api/agencywhatsapp/status" });
      setStatus(data.connected ? data : false);
    } catch {
      setStatus(false);
    } finally {
      setLoading(false);
    }
  }, [token, agencyId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleToggle = async () => {
    if (!token) return;
    setActionLoading(true);
    try {
      // The backend flips the value; trust the returned isEnabled.
      const data = await agencyApi({ token, agencyId, path: "api/agencywhatsapp/toggle", method: "PUT" });
      if (data.success) {
        setStatus((prev) => (prev ? { ...prev, isEnabled: data.isEnabled } : false));
      }
    } catch (e) {
      onError(e?.message ?? "Failed to update automation. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!token) return;
    setActionLoading(true);
    try {
      const data = await agencyApi({
        token,
        agencyId,
        path: "api/whatsapp/onboarding/disconnect",
        method: "POST",
      });
      if (data?.success === false) throw new Error("Failed to disconnect. Please try again.");
      setStatus(false);
      setConfirmOpen(false);
      if (onDisconnected) onDisconnected();
    } catch (e) {
      onError(e?.message ?? "Failed to disconnect. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleTemplate = async (templateName, languageCode) => {
    if (!token) return;
    setActionLoading(true);
    try {
      await agencyApi({
        token,
        agencyId,
        path: "api/agencywhatsapp/template",
        method: "PUT",
        body: { templateName, languageCode },
      });
    } catch (e) {
      onError(e?.message ?? "Failed to update welcome template. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Card variant="outlined" sx={{ mb: 4, borderRadius: 4, borderColor: tokens.border }}>
        <Box sx={{ height: 4, bgcolor: tokens.wa, opacity: 0.3 }} />
        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
          <CardHeaderRow
            icon={<BsWhatsapp size={24} color="#fff" />}
            iconBg={tokens.wa}
            iconShadow={`0 6px 16px -6px ${tokens.wa}80`}
            title="WhatsApp Connection"
            subtitle="Settings for the agency's own WhatsApp number"
          >
            <CircularProgress size={26} thickness={4} sx={{ color: tokens.wa }} />
          </CardHeaderRow>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ mb: 4, borderRadius: 4, borderColor: tokens.border }}>
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${tokens.wa} 0%, #6DD580 50%, ${tokens.wa} 100%)` }} />
      <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
        <CardHeaderRow
          icon={<BsWhatsapp size={22} color="#fff" />}
          iconBg={tokens.wa}
          iconShadow={`0 6px 16px -6px ${tokens.wa}80`}
          title="WhatsApp Connection"
          subtitle="Settings for the agency's own WhatsApp number"
        >
          {status && status.connected && (
            <Chip icon={<LiveDot />} label="Connected" size="small" sx={{ bgcolor: tokens.successBg, color: tokens.success, fontWeight: 700, pl: 0.5 }} />
          )}
        </CardHeaderRow>

        {!status || !status.connected ? (
          <Box sx={{ py: 1 }}>
            <EmptyState
              icon={<BsWhatsapp size={20} color={tokens.inkFaint} />}
              title="Agency WhatsApp not connected"
              caption="Connect the agency's own WhatsApp number under Integration Hub before using the agency inbox."
            />
          </Box>
        ) : (
          <>
            <Divider sx={{ my: 3, borderColor: tokens.border }} />

            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={6} md={3}>
                <InfoTile label="Business" value={status.businessName || "—"} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoTile label="Number" value={status.displayPhoneNumber || "—"} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoTile label="Connected" value={status.connectedAt ? new Date(status.connectedAt).toLocaleString() : "—"} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoTile label="Status" value={status.isEnabled ? "Enabled" : "Paused"} />
              </Grid>
            </Grid>

            {/* Enable / pause switch */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                p: 2,
                borderRadius: 2.5,
                bgcolor: status.isEnabled ? tokens.waLight : tokens.warningBg,
                border: `1px solid ${status.isEnabled ? "rgba(37,211,102,0.25)" : "rgba(245,158,11,0.25)"}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: status.isEnabled ? tokens.wa : tokens.warning,
                    boxShadow: status.isEnabled ? `0 0 8px ${tokens.waGlow}` : `0 0 8px rgba(245,158,11,0.25)`,
                    transition: "all 0.15s ease",
                  }}
                />
                <Typography variant="body2" fontWeight={700} sx={{ color: tokens.ink }}>
                  Automation: {status.isEnabled ? "Active" : "Paused"}
                </Typography>
                {status.isEnabled ? (
                  <Typography variant="caption" sx={{ color: tokens.inkFaint }}>
                    Agency replies go out from the agency number.
                  </Typography>
                ) : (
                  <Typography variant="caption" sx={{ color: tokens.inkMuted }}>
                    Paused — new messages are not being sent automatically.
                  </Typography>
                )}
              </Box>
              <Button variant="outlined" size="small" onClick={handleToggle} disabled={actionLoading} sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}>
                {actionLoading ? (
                  <CircularProgress size={14} thickness={5} />
                ) : status.isEnabled ? (
                  <><FiPause size={14} sx={{ marginRight: 0.5 }} />Pause</>
                ) : (
                  <><FiPlay size={14} sx={{ marginRight: 0.5 }} />Resume</>
                )}
              </Button>
            </Box>

            {/* Welcome template settings */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: tokens.ink, mb: 1 }}>
                Welcome template
              </Typography>
              <Paper sx={{ p: 2, borderRadius: 2.5, bgcolor: tokens.surface, border: `1px solid ${tokens.border}` }} elevation={0}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Template name"
                      placeholder="e.g. hello_world"
                      disabled={!status.isEnabled || actionLoading}
                      sx={{ mt: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Language"
                      placeholder="en_US"
                      disabled={!status.isEnabled || actionLoading}
                      sx={{ mt: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button
                      variant="contained"
                      fullWidth
                      disabled={!status.isEnabled || actionLoading}
                      startIcon={<FiRefreshCw size={15} />}
                      sx={{ mt: 1, textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}
                    >
                      Save template
                    </Button>
                  </Grid>
                </Grid>
                <Typography variant="caption" sx={{ color: tokens.inkFaint, mt: 1, display: "block" }}>
                  Used for the first message when a lead is outside the 24-hour window.
                </Typography>
              </Paper>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2.5 }}>
              <Button
                variant="text"
                size="small"
                onClick={() => setConfirmOpen(true)}
                disabled={actionLoading}
                startIcon={<FiX size={14} />}
                sx={{ textTransform: "none", fontWeight: 600, color: tokens.danger, "&:hover": { bgcolor: tokens.dangerBg } }}
              >
                Disconnect
              </Button>
            </Box>
            <ConfirmDialog
              open={confirmOpen}
              title="Disconnect WhatsApp?"
              message="Automated messages and the agency inbox will stop working until you connect a number again."
              confirmLabel="Disconnect"
              loading={actionLoading}
              onConfirm={handleDisconnect}
              onClose={() => setConfirmOpen(false)}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ================================================================
// AGENCY WHATSAPP INBOX — agency's own conversations
// ================================================================
function AgencyWhatsAppInbox({ agencyId, token, onError }) {
  const tokens = useTokens();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshList, setRefreshList] = useState(false);
  const [error, setError] = useState(null);

  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [convLoading, setConvLoading] = useState(false);
  const [convError, setConvError] = useState(null);
  const [refreshingConv, setRefreshingConv] = useState(false);

  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const fetchConversations = useCallback(
    async (silent = false) => {
      if (!token || !agencyId) return;
      if (!silent) {
        setLoading(true);
      } else {
        setRefreshList(true);
      }
      setError(null);
      try {
        const data = await agencyApi({ token, agencyId, path: "api/agencywhatsapp/conversations" });
        setConversations(Array.isArray(data) ? data : []);
      } catch {
        setConversations([]);
        setError("Could not load agency conversations. Please try again.");
      } finally {
        setLoading(false);
        setRefreshList(false);
      }    },
    [token, agencyId]
  );

  useEffect(() => {
    fetchConversations();}, [fetchConversations]);

  const loadConversation = useCallback(
    async (convId, { silent } = {}) => {
      if (!convId) return;
      if (!silent) {
        setConvLoading(true);
        setMessages([]);
      } else {
        setRefreshingConv(true);
      }
      try {
        const data = await agencyApi({ token, agencyId, path: `api/agencywhatsapp/conversation/${convId}` });
        setMessages(normalizeAgencyMessages(data));
        setConvError(null);
      } catch {
        if (!silent) {
          setMessages([]);
          setConvError("Could not load this conversation. Please try again.");
        }
      } finally {
        setConvLoading(false);
        setRefreshingConv(false);
      }
    },
    [token, agencyId]
  );

  const openConversation = (conv) => {
    setActiveConv({
      id: conv.Id,
      phoneNumber: conv.PhoneNumber,
      status: conv.Status,
    });
    loadConversation(conv.Id);
  };

  // Poll the open chat so the agency sees new inbound messages quickly.
  useEffect(() => {
    if (!activeConv?.id) return undefined;
    const interval = setInterval(() => {
      loadConversation(activeConv.id, { silent: true });
    }, 20000);
    return () => clearInterval(interval);
  }, [activeConv?.id, loadConversation]);

  const sendMessage = async () => {
    const message = draft.trim();
    if (!message || !activeConv?.id || sending) return;
    setSending(true);
    try {
      const data = await agencyApi({
        token,
        agencyId,
        path: `api/agencywhatsapp/send/${activeConv.id}`,
        method: "POST",
        body: { message },
      });
      if (data.success || data.messageId) {
        setDraft("");
        setMessages((prev) => [
          ...prev,
          {
            id: `local-${Date.now()}`,
            text: message,
            role: "Agency",
            ts: new Date().toISOString(),
            direction: "Outgoing",
          },
        ]);
        loadConversation(activeConv.id, { silent: true });
      }
    } catch (e) {
      onError(e?.message ?? "Message failed to send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, convLoading, activeConv?.id]);

  return (
    <>
      <Divider sx={{ my: 3, borderColor: tokens.border }} />

      {/* Toolbar */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <FiInbox size={17} color={tokens.waDark} />
          <Typography variant="subtitle2" fontWeight={800} sx={{ color: tokens.ink }}>
            Agency Inbox
          </Typography>
          <Chip label={`${conversations.length} chat${conversations.length === 1 ? "" : "s"}`} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: tokens.waLight, color: tokens.waDark }} />
        </Stack>
        <Tooltip title="Refresh conversations">
          <IconButton
            size="small"
            onClick={() => {
              fetchConversations(true);
              if (activeConv?.id) loadConversation(activeConv.id, { silent: true });
            }}
            disabled={refreshList}
            sx={{
              color: tokens.inkMuted,
              border: `1px solid ${tokens.border}`,
              borderRadius: 1.5,
              ...(refreshList
                ? { "& svg": { animation: "waSpin 0.9s linear infinite" }, "@keyframes waSpin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } } }
                : {}),
            }}
          >
            <FiRefreshCw size={14} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ border: `1px solid ${tokens.border}`, borderRadius: 3, overflow: "hidden", bgcolor: tokens.surface }}>
        {loading ? (
          <Box sx={{ p: 1.5 }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rounded" height={52} sx={{ mx: 0.5, my: 0.75, borderRadius: 2 }} />
            ))}
          </Box>
        ) : error ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, py: 4 }}>
            <FiAlertCircle size={22} color={tokens.danger} />
            <Typography variant="body2" sx={{ color: tokens.inkMuted }}>{error}</Typography>
            <Button size="small" variant="outlined" onClick={() => fetchConversations()} sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}>
              Retry
            </Button>
          </Box>
        ) : conversations.length === 0 ? (
          <EmptyState icon={<FiInbox size={20} color={tokens.inkFaint} />} title="No agency conversations yet" caption="Chats from the agency's own WhatsApp number will appear here as they come in." />
        ) : (
          <Grid container>
            {/* Left pane: conversation list */}
            <Grid item xs={12} md={4} sx={{ borderRight: { md: `1px solid ${tokens.border}` }, borderBottom: { xs: `1px solid ${tokens.border}`, md: "none" }, maxHeight: { xs: 300, md: 480 }, overflowY: "auto", py: 1 }}>
              {conversations.map((conv) => {
                const isActive = activeConv?.id === conv.Id;
                return (
                  <AgencyConvRow
                    key={conv.Id}
                    conv={conv}
                    onClick={() => openConversation(conv)}
                    sx={{
                      ...(isActive
                        ? { bgcolor: tokens.waLight, borderColor: "rgba(37,211,102,0.4)" }
                        : {}),
                    }}
                  />
                );
              })}
            </Grid>

            {/* Right pane: chat */}
            <Grid item xs={12} md={8} sx={{ display: "flex", flexDirection: "column" }}>
              {!activeConv ? (
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <EmptyState icon={<FiMessageCircle size={20} color={tokens.inkFaint} />} title="Select a conversation" caption="Pick a chat on the left to read the conversation and reply as the agency." />
                </Box>
              ) : (
                <>
                  {/* Chat header */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.5, borderBottom: `1px solid ${tokens.border}`, bgcolor: tokens.surface }}>
                    <Avatar sx={{ bgcolor: tokens.wa, width: 36, height: 36, color: "#fff" }}>
                      <FiPhone size={18} />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={800} noWrap sx={{ color: tokens.ink }}>
                        {prettyPhone(activeConv.phoneNumber)}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.25 }}>
                        <Typography variant="caption" sx={{ color: tokens.inkFaint }} noWrap>
                          via agency number
                        </Typography>
                        <StatusPill status={activeConv.status} />
                      </Stack>
                    </Box>
                    <Tooltip title="Refresh messages">
                      <IconButton
                        size="small"
                        onClick={() => loadConversation(activeConv.id, { silent: true })}
                        disabled={refreshingConv}
                        sx={{
                          color: tokens.inkMuted,
                          ...(refreshingConv
                            ? { "& svg": { animation: "waSpin2 0.9s linear infinite" }, "@keyframes waSpin2": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } } }
                            : {}),
                        }}
                      >
                        <FiRefreshCw size={15} />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Messages */}
                  <Box sx={{ height: { xs: 300, md: 360 }, overflowY: "auto", bgcolor: tokens.chatBg, px: 2, py: 2 }}>
                    {convLoading ? (
                      <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <CircularProgress size={24} thickness={4} sx={{ color: tokens.wa }} />
                      </Box>
                    ) : convError ? (
                      <Box sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
                        <FiAlertCircle size={22} color={tokens.danger} />
                        <Typography variant="body2" sx={{ color: tokens.inkMuted }}>{convError}</Typography>
                        <Button size="small" variant="outlined" onClick={() => loadConversation(activeConv.id)} sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}>
                          Retry
                        </Button>
                      </Box>
                    ) : messages.length === 0 ? (
                      <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <EmptyState icon={<FiMessageCircle size={20} color={tokens.inkFaint} />} title="No messages yet" caption="This conversation has no messages. Say hello as the agency below." />
                      </Box>
                    ) : (
                      <Stack spacing={1.25}>
                        {messages.map((msg) => (
                          <MessageBubble key={msg.id} msg={msg} />
                        ))}
                      </Stack>
                    )}
                  </Box>

                  {/* Composer */}
                  <Box sx={{ borderTop: `1px solid ${tokens.border}`, bgcolor: tokens.surface, p: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
                      <InputBase
                        fullWidth
                        multiline
                        maxRows={4}
                        placeholder="Write a message — it will be sent from the agency's WhatsApp…"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            sendMessage();
                          }
                        }}
                        sx={{ bgcolor: tokens.chatBg, borderRadius: 3, px: 1.75, py: 1, fontSize: 14, color: tokens.ink }}
                      />
                      <IconButton
                        onClick={sendMessage}
                        disabled={sending || !draft.trim()}
                        sx={{
                          bgcolor: tokens.wa,
                          color: "#fff",
                          "&:hover": { bgcolor: tokens.waDark },
                          "&.Mui-disabled": { bgcolor: "rgba(37,211,102,0.4)", color: "#fff" },
                          width: 40,
                          height: 40,
                        }}
                      >
                        {sending ? (
                          <CircularProgress size={17} thickness={5} sx={{ color: "#fff" }} />
                        ) : (
                          <FiSend size={16} />
                        )}
                      </IconButton>
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.75, px: 0.5 }}>
                      <FiShield size={11} color={tokens.inkFaint} />
                      <Typography variant="caption" sx={{ color: tokens.inkFaint }}>
                        Sent through the agency's own WhatsApp account.
                      </Typography>
                    </Stack>
                  </Box>
                </>
              )}
            </Grid>
          </Grid>
        )}
      </Box>
    </>
  );
}

// ================================================================
// AGENCY WHATSAPP INBOX CARD — wrapper with header
// ================================================================
export function AgencyWhatsAppInboxCard({ agencyId, token, onError }) {
  const tokens = useTokens();
  return (
    <Card variant="outlined" sx={{ mb: 4, borderRadius: 4, borderColor: tokens.border, overflow: "hidden", boxShadow: "0 1px 2px rgba(20,23,31,0.04)" }}>
      <Box sx={{ height: 4, background: `linear-gradient(90deg, #128C7E 0%, ${tokens.wa} 50%, #128C7E 100%)` }} />
      <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
        <CardHeaderRow
          icon={<FiInbox size={22} color="#fff" />}
          iconBg="#128C7E"
          iconShadow="0 6px 16px -6px rgba(18,140,126,0.5)"
          title="Agency Inbox"
          subtitle="Read and reply to conversations on the agency's own WhatsApp number"
        >
          <Chip icon={<FiShield />} label="Agency number" size="small" sx={{ bgcolor: "rgba(18,140,126,0.08)", color: "#128C7E", fontWeight: 700, pl: 0.5 }} />
        </CardHeaderRow>
        <AgencyWhatsAppInbox agencyId={agencyId} token={token} onError={onError} />
      </CardContent>
    </Card>
  );
}

// ================================================================
// TEAM INBOX CARD — owner-facing wrapper
// ================================================================
export function TeamInboxCard({ agencyId, token, onError }) {
  const tokens = useTokens();
  return (
    <Card
      variant="outlined"
      sx={{
        mb: 4,
        borderRadius: 4,
        borderColor: tokens.border,
        overflow: "hidden",
        boxShadow: "0 1px 2px rgba(20,23,31,0.04)",
      }}
    >
      {/* brand gradient hairline — WhatsApp teal-green */}
      <Box
        sx={{
          height: 4,
          background: `linear-gradient(90deg, #128C7E 0%, ${tokens.wa} 50%, #128C7E 100%)`,
        }}
      />

      <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
        <CardHeaderRow
          icon={<FiInbox size={22} color="#fff" />}
          iconBg="#128C7E"
          iconShadow="0 6px 16px -6px rgba(18,140,126,0.5)"
          title="Team Inbox"
          subtitle="Read every agent's WhatsApp chats and step in as the agency when needed"
        >
          <Chip
            icon={<FiShield />}
            label="Owner view"
            size="small"
            sx={{
              bgcolor: "rgba(18,140,126,0.08)",
              color: "#128C7E",
              fontWeight: 700,
              pl: 0.5,
              "& .MuiChip-icon": { ml: 1, mr: -0.5 },
            }}
          />
        </CardHeaderRow>

        <TeamInbox agencyId={agencyId} token={token} onError={onError} />
      </CardContent>
    </Card>
  );
}

// ---- Small info tile used inside the connected state ----
export function InfoTile({ label, value, icon }) {
  const tokens = useTokens();
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2.5,
        bgcolor: tokens.surface,
        border: `1px solid ${tokens.border}`,
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: tokens.inkFaint, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}
      >
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
        {icon}
        <Typography variant="body2" fontWeight={700} sx={{ color: tokens.ink }}>
          {value}
        </Typography>
      </Stack>
    </Box>
  );
}


// ---- Reusable confirmation popup (disconnect / destructive actions) ----
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  loading = false,
  danger = true,
  onConfirm,
  onClose,
}) {
  const tokens = useTokens();
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, bgcolor: tokens.surface, backgroundImage: "none" } }}
    >
      <DialogTitle sx={{ fontWeight: 800, color: tokens.ink, pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: danger ? tokens.dangerBg : tokens.softFaint,
              color: danger ? tokens.danger : tokens.ink,
            }}
          >
            <FiAlertCircle size={18} />
          </Box>
          <span>{title}</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
          {message}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{ textTransform: "none", fontWeight: 700, color: tokens.inkMuted }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={14} thickness={5} sx={{ color: "#fff" }} /> : null}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 2,
            boxShadow: "none",
            bgcolor: danger ? tokens.danger : undefined,
            "&:hover": { bgcolor: danger ? "#B71C1C" : undefined },
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
